import {
  citationEntryKey,
  detectCitationMarkerStyle,
  extractCitedKeys,
  type CitationEntry,
} from "@/components/chat/citationUtils";

/**
 * Collect classic legacy markdown [N](citation:N) indices only.
 * Matches RagAdapter::CitationMarkers.legacy_citation_href_indices — no bare [N],
 * no plain "citation:N" outside a markdown link target, and no plain inventeds
 * such as [cite](citation:7).
 */
export function collectCitationHrefIndices(content: string | undefined): Set<number> {
  const text = content || "";
  const indices = new Set<number>();
  for (const match of text.matchAll(/\[(\d+)\]\(\s*<?citation:(\d+)>?\s*\)/g)) {
    indices.add(Number(match[2]));
  }
  return indices;
}

/** Collect citation indices referenced in message content (href + bare [N]). */
export function collectCitedIndices(content: string | undefined): Set<number> {
  const text = content || "";
  const indices = collectCitationHrefIndices(text);
  for (const match of text.matchAll(/\[(\d+)\](?!\()/g)) {
    indices.add(Number(match[1]));
  }
  return indices;
}

function countInRange(indices: Set<number>, totalCitations: number): number {
  let count = 0;
  for (let citationNumber = 1; citationNumber <= totalCitations; citationNumber++) {
    if (indices.has(citationNumber)) count++;
  }
  return count;
}

/** Count cited references that fall in `1..totalCitations` (href + bare [N]). */
export function countCitedReferences(content: string | undefined, totalCitations: number): number {
  return countInRange(collectCitedIndices(content), totalCitations);
}

/** Count citation:N href references in `1..totalCitations` (Answer Reasoning gate). */
export function countCitationHrefReferences(
  content: string | undefined,
  totalCitations: number
): number {
  return countInRange(collectCitationHrefIndices(content), totalCitations);
}

/** Mirror RagAdapter::CitationMarkers.label_and_body for citation metadata entries. */
export function citationLabelAndBody(item: unknown): [string, string] {
  if (item && typeof item === "object" && !Array.isArray(item)) {
    const entries = Object.entries(item as Record<string, unknown>);
    if (entries.length > 0) {
      const [key, value] = entries[0];
      return [String(key), value == null ? "" : String(value)];
    }
    // Ruby Hash#to_s for {} is "{}"
    return ["Reference", "{}"];
  }
  if (item == null) return ["", ""];
  // Match CitationBuilder / citationEntryKey: the string itself is the key.
  const text = String(item);
  return [text, text];
}

function isBlankBody(body: string): boolean {
  // Match Rails String#blank? / ActiveSupport blank? for body text.
  return body.trim().length === 0;
}

/**
 * Count citation:N refs whose citation entries have non-blank bodies.
 * Mirrors ResearchAssistant::GenerateAnswerReasoning#build_cited_sources.
 */
export function countCitedSourcesWithBody(
  content: string | undefined,
  citations: Array<unknown> | undefined
): number {
  const list = (citations ?? []) as CitationEntry[];
  if (list.length === 0) return 0;

  const labels = list.map(citationEntryKey).filter(Boolean);
  const style = detectCitationMarkerStyle(content || "", labels);

  if (style === "direct") {
    const citedKeys = new Set(extractCitedKeys(content || "", labels));
    let count = 0;
    for (const entry of list) {
      const key = citationEntryKey(entry);
      if (!key || !citedKeys.has(key)) continue;
      const [, body] = citationLabelAndBody(entry);
      if (!isBlankBody(body)) count++;
    }
    return count;
  }

  let count = 0;
  for (const index of collectCitationHrefIndices(content)) {
    if (index < 1 || index > list.length) continue;
    const item = list[index - 1];
    if (item == null) continue;
    const [, body] = citationLabelAndBody(item);
    if (!isBlankBody(body)) count++;
  }
  return count;
}

export function isWebUrl(value: string): boolean {
  return /^(https?:\/\/|www\.)\S+$/i.test(value.trim());
}

export function citationHref(value: string): string {
  const trimmed = value.trim();
  return /^www\./i.test(trimmed) ? `https://${trimmed}` : trimmed;
}
